import { Sports } from '../constants/sportType';
import { OddResponse } from '../odds/types/odds';
import {
  IConfigDate,
  IMultisportConfigEntityLinks,
  IMultisportEventsDataLabels,
  IOddsV2,
  OptionsDisplay,
  RefreshTimes,
  SDKDateOptions,
  Themes,
  WidgetType,
} from './common';

export enum SportTypes {
  ALL = 'all',
  FOOTBALL = 'football',
  BASKETBALL = 'basketball',
  TENNIS = 'tennis',
  CYCLING = 'cycling',
  HANDBALL = 'handball',
  ICE_HOCKEY = 'ice_hockey',
  MOTORSPORTS = 'motorsports',
  RUGBY_LEAGUE = 'rugby_league',
  CRICKET = 'cricket',
  ROLLER_HOCKEY = 'roller_hockey',
}

export interface DisplayAsset {
  url: string;
}

export interface Country {
  id: string;
  name: string;
  slug: string;
  entity_type: string;
  display_asset: DisplayAsset;
}

export type CompetitionsDropdownType = {
  label: string;
  sport: SportTypes;
  value?: string;
  logo?: string;
};

export interface CompetitionType {
  id: string;
  name: string;
  slug?: string;
  entity_type?: string;
  sport: string;
  display_asset?: DisplayAsset;
  country_id?: string;
  country?: Country;
  gender?: string;
}

export interface CompetitionEntry {
  competition: CompetitionType;
  event_count: number;
}

export interface SportData {
  sport: string;
  competitions: CompetitionEntry[];
}

export enum StatusType {
  FINISHED = 'FINISHED',
  NOT_STARTED = 'NOT_STARTED',
  LIVE = 'LIVE',
  INTERRUPTED = 'INTERRUPTED',
  CANCELED = 'CANCELED',
  UNKNOWN = 'UNKNOWN',
}

export type EventResultType = {
  value: string | number | null;
  tiebreakValue: number | null;
  winner: WinnerType | null;
  type: string | null;
};

export enum TennisCode {
  GAME_SCORE = 'gamescore',
  SET_1 = 'set1',
  SET_2 = 'set2',
  SET_3 = 'set3',
  SET_4 = 'set4',
  SET_5 = 'set5',
  SET_6 = 'set6',
}

export enum WinnerType {
  YES = 'YES',
  NO = 'NO',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export type EventParticipantType = {
  id: string;
  name: string;
  logo: string | null;
  score: string | number | null;
  duration: string | null;
  isWinner: boolean;
  results: EventResultType[];
};

interface ICompetition {
  id: string;
  name: string;
  type: string;
  sport: string;
  countryUrl: string;
  logoUrl: string;
  slug: string;
  legacyId?: string;
}

export interface MutlisportSdkOptions extends SDKDateOptions {
  dataConfigApiUrl: string;
  dataConfigApiKey: string;
  dataConfigProject: string;
  dataConfigTimezone: string;
  dataConfigCompetitionList: string;
  dataConfigOddClient: string;
}

export interface IMultisportEvent {
  id: string;
  sport: string | null;
  date: string | null;
  statusType: StatusType;
  statusText: string | null;
  stage: string | null;
  sides: {
    first: EventParticipantType;
    second: EventParticipantType;
  } | null;
  participants: EventParticipantType[] | null;
  slug: string | null;
  odds: IOddsV2;
  legacyId?: string | null;
}

export interface ICompetitionEvents {
  competition: ICompetition;
  events: IMultisportEvent[];
}

export interface ICompetitionEventCount {
  competition: ICompetition;
  eventCount: number;
}

export interface IMultisportCompetition {
  sport: string;
  competitions: ICompetitionEventCount[];
}

export interface IMultisportCompetitionEvents {
  sport: string;
  competitions: ICompetitionEvents[];
}

export interface IEventsProps {
  date: string;
  sport?: SportTypes;
  competitionIds?: string;
  nameType?: string;
  dataOddsBettingId?: IMultisportEventsConfig['dataOddsBettingId'];
  dataOddsDisplay?: IMultisportEventsConfig['dataOddsDisplay'];
  dataOddsPreEventOnly?: IMultisportEventsConfig['dataOddsPreEventOnly'];
}

export interface ICompetitionEventsProps {
  date: string;
  sport?: SportTypes | null;
  competitionIds?: string[] | null;
  nameType?: string | null;
  limit?: number | null;
  dataOddsBettingId?: IMultisportEventsConfig['dataOddsBettingId'];
  dataOddsDisplay?: IMultisportEventsConfig['dataOddsDisplay'];
  dataOddsPreEventOnly?: IMultisportEventsConfig['dataOddsPreEventOnly'];
}

export interface IMultisportApi {
  competitions({ date }: { date: string }): Promise<IMultisportCompetition[]> | undefined;

  events(data: IEventsProps): Promise<IMultisportCompetitionEvents> | undefined;

  competitionEvents(data: ICompetitionEventsProps): Promise<IMultisportCompetitionEvents> | undefined;
}

export interface IUseMultisportApi {
  $multisport?: IMultisportApi;
}

export interface IMultisportEventsConfig {
  dataTheme?: Themes | null;
  dataWidgetSport: Sports;
  dataWidgetId: string;
  dataWidgetCuid?: string;
  dataWidgetType: WidgetType;
  dataEntityLinks?: IMultisportConfigEntityLinks | null;
  dataLabels?: IMultisportEventsDataLabels | null;
  dataOddsBettingId?: string | null;
  dataOddsDisplay?: boolean;
  dataOddsPreEventOnly?: boolean;
  dataAgeRestrictionDisplay?: string;
  dataOptionsDisplay?: OptionsDisplay;
  dataTeamNameType?: string;
  dataBettingLogoDisplay?: boolean;
  dataOddsClickable?: boolean;
  dataRefreshTime?: RefreshTimes;
  dataShowCompetitionNames?: boolean;
  dataCompetitions?: string[] | null;
  dataSport?: SportTypes | null;
  dataLimit?: number | null;
  dataDate?: IConfigDate | null;
}

export interface IMultiSportEventsResponse {
  competitions: IMultiSportCompetition[];
}

export interface IMultiSportCompetition {
  competition: Competition;
  events: Event[];
}
export interface Competition {
  name: string;
  short_name: string | null;
  three_letter_code: string | null;
  id: string;
  region: string;
  sport: string;
  display_asset: Asset;
  country?: ICountry;
  country_id: string;
  gender: string;
  entity_type: string;
  slug: string;
}

interface Asset {
  url: string;
  timestamp: string | null;
  original_filename: string | null;
}

interface ICountry {
  name: string;
  short_name: string | null;
  three_letter_code: string | null;
  id: string;
  legacy_id: string;
  display_asset: Asset;
  entity_type: string;
  slug: string;
}

export interface Event {
  name: string;
  short_name: null;
  three_letter_code: string;
  translations: Translation[];
  id: string;
  legacy_id: null;
  display_asset: Asset;
  sport: string;
  status: Status;
  elapsed: string;
  status_type: string;
  participants: string[];
  results: Status[];
  start_time: string;
  stage: {
    id: string;
    name: string;
  };
  round: {
    id: string;
    name: string;
  };
  season: Season;
  competition_id: string;
  participant_details: ParticipantDetail[];
  entity_type: string;
  slug: string;
  competition: Competition;
  odds: OddResponse[];
}
export interface Translation {
  language: string;
  name: string;
  short_name: null;
  three_letter_code: null | string;
}

export interface ParticipantDetail {
  participant: Participant;
  results: Result[];
}

export interface Participant {
  name: string;
  short_name: string | null;
  three_letter_code: string | null;
  translations: Translation[];
  id: string;
  legacy_id: null;
  entity_type: string;
  slug: string;
  position: string;
  display_asset: Asset;
  winner: string;
  current_server: null;
}

export interface Result {
  type: Type;
  value: string;
  winner: string;
  tiebreak_value: null;
}

export interface Type {
  code: string;
  name: string;
}

export interface Status {
  name: string;
  short_name: string;
  three_letter_code: string;
  translations: Translation[];
  id: string;
  value?: string;
  position?: string;
  entity_type: string;
  legacy_id?: null;
  type: string;
}

export interface Season {
  name: string;
  short_name: null;
  three_letter_code: null;
  translations: Translation[];
  id: string;
  legacy_id: null;
}
export interface IMultiSportEventResponse {
  name: string;
  short_name: string | null;
  three_letter_code: string | null;
  translations: Translation[];
  id: string;
  display_asset: Asset;
  sport: string;
  status: Status;
  status_type: string;
  participants: string[];
  results: [];
  start_time: string;
  stage: [];
  season: Season;
  competition_id: string;
  participant_details: ParticipantDetail[];
  entity_type: string;
  slug: string;
  competition: Competition;
  elapsed: string | null;
  odds: OddResponse[];
}
